import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Star, Clock, ShieldCheck, Zap, Smartphone, CalendarDays, ArrowLeftRight, CupSoda, Check, Download } from 'lucide-react';
import { Button } from './Button';
import { track, PRODUCT } from '../lib/fpixel';
const BOOK_IMG = 'https://cdn.magicpatterns.com/uploads/b4BQKUnCQpZ48J6KjWDaFM/ChatGPT_Image_19_jun_2026,_03_05_32.png';
interface BonusItem {
  num: number;
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  lead: string;
  points: string[];
  value: string;
}
const BONUSES: BonusItem[] = [{
  num: 1,
  icon: CalendarDays,
  title: 'PLAN RESETEO DE 7 DÍAS',
  lead: 'Empieza sin dudas.',
  points: ['Menú completo', 'Lista de compras', 'Plan paso a paso'],
  value: '$9.990'
}, {
  num: 2,
  icon: ArrowLeftRight,
  title: 'MANUAL DE REEMPLAZOS INTELIGENTES',
  lead: 'Qué usar cuando te falta un ingrediente.',
  points: ['Harinas', 'Edulcorantes', 'Frutas', 'Sustituciones prácticas'],
  value: '$9.990'
}, {
  num: 3,
  icon: CupSoda,
  title: 'RECETARIO DE BATIDOS Y BEBIDAS',
  lead: 'Bebidas deliciosas para todos los días.',
  points: ['Batidos', 'Limonadas', 'Tés', 'Opciones refrescantes'],
  value: '$9.990'
}];
const VALUE_ROWS = [{
  label: 'Enciclopedia Gluko Libre Argentina',
  value: '$39.990'
}, {
  label: 'Bonus #1 · Plan Reseteo de 7 días',
  value: '$9.990'
}, {
  label: 'Bonus #2 · Manual de Reemplazos',
  value: '$9.990'
}, {
  label: 'Bonus #3 · Recetario de Batidos',
  value: '$9.990'
}];
const INCLUDES = [{
  icon: Zap,
  text: 'Acceso inmediato'
}, {
  icon: Download,
  text: 'Descarga digital'
}, {
  icon: Smartphone,
  text: 'Celular y compu'
}, {
  icon: ShieldCheck,
  text: 'Garantía 7 días'
}];
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds(s => s > 0 ? s - 1 : initialSeconds);
    }, 1000);
    return () => clearInterval(id);
  }, [initialSeconds]);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
function GoldBadge({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1 bg-accent text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm" data-id="element-103">
      {children}
    </span>;
}
function ValueTag({
  value
}: {
  value: string;
}) {
  return <div className="inline-flex items-baseline gap-1.5 mt-auto" data-id="element-104">
      <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold" data-id="element-105">
        Valor
      </span>
      <span className="text-base font-bold text-gray-400 line-through decoration-red-300 decoration-2" data-id="element-106">
        {value}
      </span>
    </div>;
}
export function Offer() {
  const time = useCountdown(5 * 3600 + 59 * 60 + 59);
  return <section className="py-12 md:py-16 px-4 bg-gray-50" data-id="element-107">
      <div className="max-w-4xl mx-auto" data-id="element-108">
        {/* Header */}
        <div className="text-center mb-7" data-id="element-109">
          <div className="inline-flex items-center gap-1.5 bg-accent/15 text-accent-light px-3 py-1 rounded-full font-bold text-[11px] sm:text-xs mb-3 uppercase tracking-wider" data-id="element-110">
            <Star className="w-3.5 h-3.5 fill-current" data-id="element-111" />
            Oferta especial de lanzamiento
          </div>
          <h2 className="text-2xl md:text-4xl font-serif font-bold text-gray-900 leading-tight" data-id="element-112">
            Todo lo que recibís hoy
          </h2>
        </div>

        {/* PRODUCTO PRINCIPAL — compact row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-2xl shadow-md shadow-gray-200/70 border border-gray-100 p-4 sm:p-5 flex items-center gap-4 mb-4" data-id="element-113">
          <img src={BOOK_IMG} alt="Portada de la Enciclopedia Gluko Libre Argentina" className="w-20 sm:w-24 rounded-lg shadow-md ring-1 ring-black/5 flex-shrink-0" data-id="element-114" />
          <div className="min-w-0" data-id="element-115">
            <span className="inline-flex items-center bg-primary text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full mb-1.5" data-id="element-116">
              +100 Recetas
            </span>
            <h3 className="text-base sm:text-lg font-serif font-bold text-gray-900 leading-tight" data-id="element-117">
              ENCICLOPEDIA GLUKO LIBRE ARGENTINA
            </h3>
            <p className="text-sm text-gray-500 leading-snug mt-1 mb-1.5" data-id="element-118">
              Panes, empanadas, pizzas, postres, snacks y mucho más.
            </p>
            <ValueTag value="$39.990" data-id="element-119" />
          </div>
        </motion.div>

        {/* BONUS CARDS — compact */}
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-8" data-id="element-120">
          {BONUSES.map((bonus, index) => <motion.div key={bonus.num} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08
        }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col" data-id="element-121">
              <div className="flex items-center justify-between mb-3" data-id="element-122">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-primary" data-id="element-123">
                  <bonus.icon className="w-5 h-5" data-id="element-124" />
                </div>
                <GoldBadge data-id="element-125">Bonus gratis</GoldBadge>
              </div>

              <span className="text-[10px] font-bold uppercase tracking-wider text-accent-light mb-1" data-id="element-126">
                🎁 Bonus #{bonus.num}
              </span>
              <h4 className="text-sm font-serif font-bold text-gray-900 leading-tight mb-1.5" data-id="element-127">
                {bonus.title}
              </h4>
              <p className="text-xs text-gray-600 font-medium mb-3" data-id="element-128">
                {bonus.lead}
              </p>

              <ul className="space-y-1.5 mb-4" data-id="element-129">
                {bonus.points.map((p, i) => <li key={i} className="flex items-center gap-2 text-xs text-gray-600" data-id="element-130">
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" data-id="element-131" />
                    {p}
                  </li>)}
              </ul>

              <ValueTag value={bonus.value} data-id="element-132" />
            </motion.div>)}
        </div>

        {/* VALOR TOTAL + PRECIO — the scroll target so CTAs land right here */}
        <motion.div id="oferta" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} onViewportEnter={() => track('ViewContent', {
        content_name: PRODUCT.content_name,
        content_category: PRODUCT.content_category,
        value: PRODUCT.value,
        currency: PRODUCT.currency
      })} className="scroll-mt-24 max-w-md mx-auto bg-white rounded-3xl shadow-xl shadow-gray-300/40 border-4 border-primary/15 p-5 sm:p-6 text-center" data-id="element-133">
          {/* urgency */}
          <div className="inline-flex items-center gap-1.5 text-xs text-gray-500 mb-3" data-id="element-134">
            <Clock className="w-3.5 h-3.5 text-accent" data-id="element-135" />
            Termina en
            <span className="font-mono font-bold text-gray-900 bg-gray-900/5 px-1.5 py-0.5 rounded tabular-nums" data-id="element-136">
              {time}
            </span>
          </div>

          <h3 className="text-base sm:text-lg font-serif font-bold text-gray-900 mb-3" data-id="element-137">
            Valor total del contenido
          </h3>

          <div className="space-y-1.5 text-left mb-3" data-id="element-138">
            {VALUE_ROWS.map((row, i) => <div key={i} className="flex items-center justify-between gap-3 pb-1.5 border-b border-dashed border-gray-200" data-id="element-139">
                <span className="flex items-center gap-2 text-xs sm:text-[13px] text-gray-700" data-id="element-140">
                  <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" data-id="element-141" />
                  {row.label}
                </span>
                <span className="text-xs sm:text-[13px] font-semibold text-gray-400 whitespace-nowrap" data-id="element-142">
                  {row.value}
                </span>
              </div>)}
          </div>

          {/* Total struck */}
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-143">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-144">
              Total
            </span>
            <span className="text-xl font-serif font-bold text-gray-400 line-through decoration-red-400 decoration-2" data-id="element-145">
              $69.960
            </span>
          </div>

          {/* Today price */}
          <div className="bg-primary text-white rounded-2xl py-3.5 px-4 mb-3 shadow-md shadow-primary/20" data-id="element-146">
            <p className="font-bold text-[10px] uppercase tracking-widest text-green-100 mb-0.5" data-id="element-147">
              Hoy accedés a todo por solo
            </p>
            <p className="font-serif font-black leading-none flex justify-center items-start" data-id="element-148">
              <span className="text-2xl mt-0.5 mr-0.5" data-id="element-149">$</span>
              <span className="text-[2.75rem] leading-none" data-id="element-150">19.990</span>
            </p>
            <span className="inline-block mt-2 text-[11px] font-bold uppercase tracking-widest bg-accent text-white px-3 py-1 rounded-full" data-id="element-151">
              Pago único
            </span>
          </div>

          {/* Includes */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4" data-id="element-152">
            {INCLUDES.map((inc, i) => <div key={i} className="flex items-center gap-1.5 text-xs text-gray-700 font-medium text-left" data-id="element-153">
                <inc.icon className="w-4 h-4 text-primary flex-shrink-0" data-id="element-154" />
                {inc.text}
              </div>)}
          </div>

          <Button toCheckout className="w-full text-base sm:text-lg py-4 shadow-lg shadow-primary/30" data-id="element-155">
            QUIERO ACCEDER AHORA
          </Button>

          <p className="text-[11px] text-gray-400 mt-2.5 flex items-center justify-center gap-1.5" data-id="element-156">
            🔒 Pago 100% seguro y encriptado
          </p>
        </motion.div>
      </div>
    </section>;
}